import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter, useNuxtApp } from '#app'
import { formatAsGermanDate } from '~/functions/helpers'
import {
  type AccountType,
  type GeographyType,
  type ImpactGoalType,
  useListPortfolioVentureLinksQuery,
  useUpdateVentureCommentMutation,
} from '~/types/types'


export default /*#__PURE__*/_defineComponent({
  __name: 'impactGoalList',
  props: {
    portfolio: null
  },
  setup(__props: any) {

const props = __props as {
  portfolio: AccountType
};



const route = useRoute()
const router = useRouter()

const options = ref({
  page: route.query.page
    ? parseInt(route.query.page as string, 10) || 1
    : 1,
  itemsPerPage: route.query.itemsPerPage
    ? parseInt(route.query.itemsPerPage as string, 10) || 10
    : 10,
  sortBy: (route.query.sortBy as string) || 'venture.name',
  sortDesc: route.query.sortDesc === 'true' || false,
})

watch(
  () => route.query,
  (newQuery) => {
    if (newQuery.sortBy !== options.value.sortBy) {
      options.value.sortBy = (newQuery.sortBy as string) || 'venture.name'
    }
    if (newQuery.sortDesc !== String(options.value.sortDesc)) {
      options.value.sortDesc = newQuery.sortDesc === 'true'
    }
  },
  { immediate: true, deep: true },
)

watch(
  () => ({
    page: options.value.page,
    itemsPerPage: options.value.itemsPerPage,
    sortBy: options.value.sortBy,
    sortDesc: options.value.sortDesc,
  }),
  (newOptions) => {
    // Only handle url update once
    router.replace({
      query: {
        ...route.query,
        page: newOptions.page.toString(),
        itemsPerPage: newOptions.itemsPerPage.toString(),
        sortBy: newOptions.sortBy,
        sortDesc: newOptions.sortDesc.toString(),
      },
    })
  },
  { deep: true },
)

const variables = computed(() => ({
  filters: {
    text: (route.query.search as string) || '',
    group: (route.query.group as string) || '',
    hasImpactGoal: true,
  },
  // reset page to 1 when search changes (still shown as 2 in UI and url and returns to 2 if the search is cleared)
  offset:
    route.query.search !== undefined
      ? 0
      : options.value.itemsPerPage * (options.value.page - 1),
  limit: options.value.itemsPerPage,
  sortBy: options.value.sortBy,
  sortDesc: options.value.sortDesc,
}))

const { result, loading, refetch } = useListPortfolioVentureLinksQuery(variables)
const itemCount = computed(
  () => result.value?.countPortfolioVentureLinks ?? 0,
)

const ventureLinks = computed(
  () => result.value?.listPortfolioVentureLinks ?? [],
)

const items = computed(() =>
  ventureLinks.value?.map((e: any) => {
    return {
      ...e,
      groupNames: e.groupNames.join(', '),
      geographies: [
        ...new Set(
          e?.impactGoal?.geographies?.map((e: GeographyType) => e.label),
        ),
      ].join(', '),
    }
  }),
)

const { mutate: updateVentureComment, onDone } =
  useUpdateVentureCommentMutation()
onDone(({ data }) => {
  if (data?.updateVentureComment?.comment) {
    // @ts-ignore
    window.$nuxt.$emit('userError', {
      message: 'Comment saved',
      color: 'green',
    })
  }
})

const { $i18n } = useNuxtApp()

const headers = computed(() =>
  [
    {
      text: '',
      value: 'logo',
      width: '48px',
      sort: false,
    },
    {
      text: 'Company',
      value: 'venture.name',
      width: '10%',
      align: 'center',
      sort: true,
    },
    {
      value: 'groupNames',
      sort: false,
    },
    {
      value: 'impactGoal.targetProgress',
      sort: false,
    },
    {
      value: 'impactGoal.impacts',
      width: '250px',
      sort: false,
    },
    {
      value: 'geographies',
      sort: false,
    },
    {
      value: 'impactGoal.sdgs',
      sort: false,
    },
    {
      value: 'comment',
      sort: false,
    },
    {
      value: 'impactGoal.submitDate',
      sort: true,
    },
    {
      value: 'action',
      sort: false,
    },
  ].map((e) => ({
    ...e,
    text:
      e.value === 'logo'
        ? ''
        : $i18n.t('impactGoal.listView.headers.' + e.value),
    sortable: false, // this blocks the v-data-table from trying to sort the column
  })),
)

const sortKeys = computed(() => {
  return headers.value
    .filter((v) => v.sort)
    .map(({ value }) => ({
      text: $i18n.t(`impactGoal.listView.sortBy`, {
        column: $i18n.t(`impactGoal.listView.headers.${value}`),
      }),
      value,
    }))
})

const selected = ref<Array<any>>([])
const showImpactLockDialog = ref(false)
const impactGoalForLockDialog = ref<ImpactGoalType | null>(null)

function openImpactLockDialog(impactGoal: ImpactGoalType | null | undefined) {
  if (!impactGoal) return
  impactGoalForLockDialog.value = impactGoal
  showImpactLockDialog.value = true
}


return { __sfc: true,props, route, router, options, variables, result, loading, refetch, itemCount, ventureLinks, items, updateVentureComment, onDone, $i18n, headers, sortKeys, selected, showImpactLockDialog, impactGoalForLockDialog, openImpactLockDialog, formatAsGermanDate }
}

})